import React, { useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import _set from 'lodash/set';
import _get from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Field } from '@sift/types';
import { Theme } from 'styles/ThemeTyping';
import * as Types from 'pages/Features/typings';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import BasicInfo from 'pages/Features/tabs/OrgChart/DefaultOrgChartAttrSettings/molecules/BasicInfo';
import BasicInfoClassicSimple from 'pages/Features/tabs/OrgChart/DefaultOrgChartAttrSettings/molecules/BasicInfoClassicSimple';
import Metrics from 'pages/Features/tabs/OrgChart/DefaultOrgChartAttrSettings/molecules/Metrics';
import CustomAttrs from 'pages/Features/tabs/OrgChart/DefaultOrgChartAttrSettings/molecules/CustomAttr';
import OrgLevels from 'pages/Features/tabs/OrgChart/DefaultOrgChartAttrSettings/molecules/OrgLevels';

interface Props {
  open: boolean;
  onClose: () => void;
  fields: Field[];
  values: Types.FeatureValues;
  dialogType: string;
  onChange: (field: string, value: any) => void;
}

function DefaultOrgChartAttrSettingsDialog({
  open,
  onClose,
  fields,
  values,
  dialogType,
  onChange,
}: Props) {
  const classes = useStyles();
  const [localValues, setLocalValues] = useState(values);

  const handleUpdateAttributes = () => {
    onChange('person.directory.orgChart', localValues.person.directory.orgChart);
    onClose();
  };

  const handleCancel = () => {
    onClose();
    setLocalValues(values);
  };

  const handleLocalChange = (field: string, value: any) => {
    setLocalValues(prev => {
      const next = cloneDeep(prev);
      _set(next, field, value);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <div className={classes.root}>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.column}>
            {dialogType === 'classicSimple' ? (
              <BasicInfoClassicSimple />
            ) : (
              <BasicInfo values={localValues} onChange={handleLocalChange} view={dialogType} />
            )}

            <Divider variant="fullWidth" className={classes.divider} />

            <Metrics values={localValues} onChange={handleLocalChange} view={dialogType} />
          </div>

          {(dialogType === 'modern' || dialogType === 'classicAdvanced') && (
            <Divider orientation="vertical" flexItem className={classes.divider} />
          )}

          <div>
            {dialogType === 'modern' && (
              <CustomAttrs
                onChange={handleLocalChange}
                fields={fields}
                values={localValues}
                view={dialogType}
              />
            )}

            {dialogType === 'classicAdvanced' && (
              <OrgLevels
                onChange={handleLocalChange}
                fields={fields}
                values={localValues}
                view={dialogType}
              />
            )}
          </div>
        </DialogContent>

        <Divider className={classes.divider} />

        <DialogActions>
          <div className={classes.buttonContainer}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.doneButton}
              onClick={handleUpdateAttributes}
            >
              Done
            </Button>
          </div>
        </DialogActions>
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    height: 708,
    flexDirection: 'column',
    padding: 0,
  },
  column: {
    minWidth: 378,
  },
  dialogContent: {
    display: 'flex',
    padding: 0,
    '&:first-child': {
      padding: 0,
    },
    overflowY: 'hidden',
  },
  divider: {
    margin: 0,
  },
  doneButton: {
    marginLeft: theme.spacing(2),
  },
  buttonContainer: {
    marginLeft: 'auto',
  },
}));

export default DefaultOrgChartAttrSettingsDialog;
